/**
 * What each annual plan tier includes, shared by /purchase-plan (components/plans.js,
 * the comparison table) and the Auto-Renew page (components/subscription.js, the
 * highlights on each card).
 */

export const TAMIL_NAMES = { starter: 'தொடக்கம்', growth: 'வளர்ச்சி', pro: 'பிரபலம்', premium: 'பிரீமியம்' };

// Feature comparison: [group, Tamil label, rows]; a row is [feature, tier 1, tier 2, tier 3],
// cheapest tier first, where 1 is included and 0 is not
export const FEATURES = [
    ['Store setup', 'கடை அமைப்பு', [
        ['Mobile-responsive storefront', 1, 1, 1],
        ['Custom domain connection', '1', '1', 'Unlimited'],
        ['Tamil language support', 1, 1, 1],
        ['Pre-built themes', '5', '15', 'Unlimited'],
    ]],
    ['Products & inventory', 'பொருட்கள்', [
        ['Number of products', 'Up to 50', 'Up to 150', 'Up to 500'],
        ['Images per product', '5', '10', '15'],
        ['Inventory tracking', 'Basic', 'Advanced', 'Advanced'],
        ['Low stock alerts', 0, 1, 1],
        ['Bulk product upload (CSV)', 0, 1, 1],
    ]],
    ['E-commerce', 'விற்பனை', [
        ['Shopping cart & checkout', 1, 1, 1],
        ['Payment gateway (UPI, cards)', 1, 1, 1],
        ['Order management', 1, 1, 1],
        ['GST compliance', 1, 1, 1],
        ['Cash on Delivery (COD)', 1, 1, 1],
        ['Discount codes & coupons', 0, 1, 1],
        ['Abandoned cart recovery', 0, 1, 1],
    ]],
    ['Business management', 'வணிக மேலாண்மை', [
        ['Sales analytics dashboard', 0, 1, 1],
        ['Revenue & profit reports', 0, 1, 1],
        ['Customer database (CRM)', 0, 1, 1],
        ['Sales forecasting', 0, 0, 1],
    ]],
    ['Marketing', 'மார்க்கெட்டிங்', [
        ['SEO tools', 'Basic', 'Advanced', 'Premium'],
        ['WhatsApp integration', 'Sharing', 'Business', 'Business'],
        ['Customer reviews & ratings', 1, 1, 1],
        ['Google Analytics integration', 0, 1, 1],
    ]],
    ['Support', 'உதவி', [
        ['Email support', '48-hour', 'Priority 24h', '24/7 priority'],
        ['Chat support', 0, 1, 1],
        ['Phone support (Tamil / English)', 0, 1, 1],
        ['Tamil documentation', 1, 1, 1],
    ]],
];

const ROWS = FEATURES.flatMap(([, , rows]) => rows);
const valueOf = (feature, tier) => {
    const row = ROWS.find(r => r[0] === feature);
    return row ? row[tier + 1] : undefined;
};

// The tier's headline limits, as short lines for a plan card
const HEADLINES = [
    ['Number of products', v => `${v} products`],
    ['Images per product', v => `${v} images per product`],
    ['Pre-built themes', v => `${v} pre-built themes`],
    ['Custom domain connection', v => (v === 'Unlimited' ? 'Unlimited custom domains' : `${v} custom domain`)],
    ['Inventory tracking', v => `${v} inventory tracking`],
    ['Email support', v => `${v} email support`],
];
// How many of the features this tier adds over the one below to list
const NEW_FEATURES_SHOWN = 2;

/**
 * Highlights for a plan card: the tier's headline limits, then the first features
 * it adds over the tier below. Empty for an unknown tier.
 * @param {number} tier 0 (cheapest) to 2
 * @returns {string[]}
 */
export const planHighlights = (tier) => {
    if (![0, 1, 2].includes(tier)) return [];
    const lines = HEADLINES.map(([feature, label]) => label(valueOf(feature, tier)));
    if (tier > 0) {
        const added = ROWS.filter(r => r[tier + 1] === 1 && r[tier] === 0).map(r => r[0]);
        lines.push(...added.slice(0, NEW_FEATURES_SHOWN));
    }
    return lines;
};

const TIER_BY_NAME = { starter: 0, growth: 1, pro: 1, premium: 2 };

/**
 * A plan's tier from its name (Starter / Growth / Pro / Premium); null for any other
 * name. Only the name is trusted (not price order), so a renamed or repriced plan
 * shows no highlights rather than another tier's limits (PR #295 review L2).
 * @param {object} plan a plan with plan_name or name
 * @returns {number|null}
 */
export const planTier = (plan) => {
    const name = String(plan.plan_name || plan.name || '').trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(TIER_BY_NAME, name) ? TIER_BY_NAME[name] : null;
};
